/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { IScope } from "../../../../interfaces/scopes";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import useMounted from "../../../../hooks/mounted";
import { ScopeAPI } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";

const DEFAULT_DATA: IScope = {
  id: "",
  idService: "",
  name: "",
  removeFromInProgressCount: null,
  blockItemInsertion: false,
};

const ScopeAPIService = new ScopeAPI();

export const useScopes = ({
  filter: filterInitial,
  disabledShowId,
  includes = [],
  disabledGetAll,
  defaultLimit,
  idService: idServiceInitial,
  id: idProp,
  onSave,
  goBack: goBackProp,
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
  idService?: string;
  id?: string;
  onSave?: () => void;
  goBack?: () => void;
}) => {
  const { id: idParam, idService: idServiceParam } = useParams();
  const id = idProp || idParam;
  const idService = idServiceInitial || idServiceParam;

  const [scopes, setScopes] = useState<IScope[]>([]);
  const [scope, setScope] = useState<IScope>(DEFAULT_DATA);
  const { filter, handleFilter, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit });
  const [totalItems, setTotalItems] = useState<number>(0);
  // Escopo vive dentro de um serviço, e o voltar devolve para ele
  const { goBack } = useEntityNavigation({
    basePath: `/towermanagement/services/${idService}/scopes`,
    pathBack: `/towermanagement/services/${idService}`,
    onBack: goBackProp,
  });

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleScope = (key: string, value: any, custom?: (v: any) => any) => {
    setScope((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const getAll = ({ page = 1, limit }: { page?: number; limit?: number }) => {
    setLoading(true);
    ScopeAPIService.listScopes({ includes: [...includes], filter, page, limit })
      .then(({ scopes: listScopes, limit, currentPage, totalItems }) => {
        setScopes(listScopes);
        
        setLimit(Number(limit));
        setTotalItems(Number(totalItems));
      })
      .finally(() => setLoading(false));
  };

  const search = useEntitySearch({
    list: (params) => ScopeAPIService.listScopes(params),
    key: "scopes",
    includes: ["idSite", ...includes],
    filter,
  });


  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    ScopeAPIService.showScope({ id })
      .then(({ scope: scopesShow }) => {
        setScope(scopesShow);
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    ScopeAPIService.createScope({ scope: { ...scope, idService: idService } })
      .then(() => {
        toast.success(`Serviço criado com sucesso!`);
        if (onSave) {
          onSave();
        } else {
          goBack();
        }
      })
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    ScopeAPIService.updateScope({ id: scope.id!, scope })
      .then(() => {
        toast.success(`Serviço atualizado com sucesso!`);
        if (onSave) {
          onSave();
        } else {
          goBack();
        }
      })
      .finally(() => setLoading(false));
  };

  useMounted(() => {
    getAll({ page, limit });
  }, [page, limit, filter]);

  useEffect(() => {
    handleScope("idService", idService);
    if (id) {
      if (id !== "new") get({ id: id });
      return;
    }
    if (!disabledGetAll) getAll({ limit });
  }, []);

  return {
    scopes,
    scope,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goBack,
    handleScope,
    create,
    id,
    update,
    totalItems,
    search,
    handleFilter,
    getAll,
  };
};
